/**
 * TrainingVerificationBadge — Batch 18-E13
 *
 * Read-only badge displayed on the technician-facing Passport
 * Learning Impact tab when a training session has been manager-
 * verified. ZERO actions, ZERO mutations — this is purely a visual
 * indicator of state already written by the 18-E11-hardened backend.
 *
 * Display rules:
 *   - Returns null when managerVerifiedAt is null/undefined — the
 *     badge only appears for verified sessions.
 *   - Shows "Confirmed by Manager" + the verification date.
 *   - Manager NAME is NOT displayed in this batch. The pointer
 *     column managerVerifiedBy is a UUID and resolving it to a name
 *     requires an extra join to auth.users / profiles that the
 *     technician's RLS may not allow. The component's prop interface
 *     accepts managerVerifiedBy for future use (when a safe lookup
 *     pattern is established).
 *   - Optional managerVerificationNote is rendered below the badge as
 *     small muted text. Technicians MAY see their own verification
 *     notes per the 18-E8 §6 privacy decision; creators / unrelated
 *     users must not — this component is only wired into Passport
 *     surfaces where the viewer is the row owner.
 *   - completionConfidence is reserved for future visual tuning
 *     (e.g. tone the badge differently when 'verified' was set by
 *     a non-manager evidence source); currently unused.
 *
 * This component does NOT:
 *   - call useVerifyTrainingCompletion or any other mutation hook
 *   - write to training_consumption_sessions or
 *     training_completion_verifications
 *   - read or write training_effectiveness_events
 *   - emit a 'manager_verified' event-type literal
 *   - reference session_token / extension_evidence / Companion code
 *   - implement revocation or queue navigation
 */

import { ShieldCheck } from 'lucide-react';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { getVerificationProvenanceLabel } from '@/utils/training/evidenceProvenanceLabels';

interface TrainingVerificationBadgeProps {
  managerVerifiedAt?: string | null;
  managerVerifiedBy?: string | null;
  managerVerificationNote?: string | null;
  completionConfidence?: string | null;
}

export function TrainingVerificationBadge({
  managerVerifiedAt,
  managerVerificationNote,
}: TrainingVerificationBadgeProps) {
  if (!managerVerifiedAt) return null;

  let dateLabel: string | null = null;
  try {
    dateLabel = format(new Date(managerVerifiedAt), 'MMM d, yyyy');
  } catch {
    dateLabel = null;
  }

  return (
    <div
      className="space-y-1"
      data-testid="training-verification-badge"
    >
      <Badge
        variant="default"
        className="bg-emerald-600 hover:bg-emerald-600 text-white gap-1"
      >
        <ShieldCheck className="h-3 w-3" />
        <span>Confirmed by Manager</span>
        {dateLabel && (
          <span className="opacity-80 font-normal">· {dateLabel}</span>
        )}
      </Badge>
      {/* Batch 18-F15.1 — provenance line (Option B). Names the trust
          class behind the badge so the viewer can't confuse manager
          verification with extension or provider evidence. */}
      <p
        className="text-[10px] text-muted-foreground/80 uppercase tracking-wide"
        data-testid="training-verification-provenance"
      >
        {getVerificationProvenanceLabel()}
      </p>
      {managerVerificationNote && (
        <p className="text-xs text-muted-foreground italic">
          “{managerVerificationNote}”
        </p>
      )}
    </div>
  );
}
